// src/components/padrekko/qc/RevokeShareModal.jsx
//
// REM-576 — the owner withdraws the share. A reason is required (the server
// refuses without one, and records it in the case history, never on the
// rail). The respondent loses access at once; at operator_review the server
// also moves the case back to customer_review, so the owner can share again,
// and a new share must name a new deadline.

import { useState } from "react";
import { Loader2, X as XIcon, ShieldOff } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { toast } from "../../../stores/useToastStore";
import ActionError from "./ActionError";

export default function RevokeShareModal({ isOpen, onClose, operatorName, movesBack = false }) {
  const caseInfo            = useQcStore((s) => s.caseInfo);
  const revokeShare         = useQcStore((s) => s.revokeShare);
  const revokeShareInflight = useQcStore((s) => s.revokeShareInflight);

  const [reason, setReason] = useState("");
  const [failure, setFailure] = useState(null);

  if (!isOpen || !caseInfo) return null;

  function close() {
    if (revokeShareInflight) return;
    setReason("");
    setFailure(null);
    onClose();
  }

  async function submit() {
    if (!reason.trim()) return;
    setFailure(null);
    const r = await revokeShare({ reason: reason.trim() });
    if (!r.ok) { setFailure(r); return; }
    toast.success(r.moved_back
      ? `Share with ${operatorName} revoked; the case is back in Customer Review`
      : `Share with ${operatorName} revoked`);
    setReason("");
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && close()}
      role="dialog"
      aria-modal="true"
      aria-label={`Revoke the share with ${operatorName}`}
    >
      <div className="w-full max-w-md bg-bg-soft border border-slate-700 rounded-xl shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-1.5">
            <ShieldOff size={12} className="text-amber-400" />
            Revoke the share with {operatorName}?
          </h3>
          <button
            type="button"
            onClick={close}
            disabled={revokeShareInflight}
            className="text-fg-secondary hover:text-slate-200 transition disabled:opacity-40"
            title="Cancel"
            aria-label="Cancel"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-[11px] text-amber-200/90 leading-relaxed">
            {operatorName} loses access immediately. A new share needs a new deadline.
            {movesBack ? " The case moves back to Customer Review." : ""}
          </p>

          <div className="space-y-1.5">
            <label htmlFor="qc-revoke-reason" className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
              Reason (required)
            </label>
            <textarea
              id="qc-revoke-reason"
              autoFocus
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (failure) setFailure(null); }}
              disabled={revokeShareInflight}
              rows={3}
              placeholder="Recorded in the case history."
              className="w-full text-[11px] px-2.5 py-2 rounded border border-slate-700 bg-slate-900 text-slate-100
                         placeholder:text-slate-600 focus:outline-none focus:border-slate-500
                         disabled:opacity-50 leading-snug resize-none"
            />
          </div>

          <ActionError failure={failure} />
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex justify-end gap-1.5">
          <button
            type="button"
            onClick={close}
            disabled={revokeShareInflight}
            className="text-[11px] px-3 py-1.5 rounded border border-slate-700 text-slate-300
                       hover:bg-slate-800 hover:border-slate-600 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!reason.trim() || revokeShareInflight}
            className="flex items-center gap-1 text-[11px] px-3 py-1.5 rounded border
                       bg-amber-500/15 border-amber-500/50 text-amber-200 hover:bg-amber-500/25
                       disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {revokeShareInflight && <Loader2 size={11} className="animate-spin" />}
            Revoke share
          </button>
        </div>
      </div>
    </div>
  );
}
